import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Users } from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { byType, typedEventNames, TypedEventType } from "../../utils/typedEvents";
import { publicTypedEventsApiFor } from "../../api/typedEventsApiFor";
import type { PublicTypedEvent } from "../../api/publicTypedEventsAPI";

/**
 * PublicCampsListPage and PublicClinicsListPage, one implementation (Phase B4,
 * backend docs/EVENT_PRIMITIVE_CONSOLIDATION.md). Built per type; the camps
 * and clinics files are the entry points routes and tests import.
 */
export function createPublicTypedEventsListPage(type: TypedEventType) {
  const T = typedEventNames(type);
  const { getPublicTypedEvents } = publicTypedEventsApiFor(type);

  interface PublicTypedEventsListPageProps {
    embed?: boolean;
  }

  const PublicTypedEventsListPage: React.FC<PublicTypedEventsListPageProps> = ({ embed }) => {
    const { clubid } = useParams<{ clubid: string }>();
    const [typedEvents, setTypedEvents] = useState<PublicTypedEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (clubid) {
        fetchTypedEvents();
      }
    }, [clubid]);

    const fetchTypedEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPublicTypedEvents(clubid!);
        if (response?.success) {
          setTypedEvents(response.data || []);
        } else {
          setError(`Failed to load ${T.nouns}`);
        }
      } catch {
        setError(`Failed to load ${T.nouns}. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    const formatDate = (dateString: string) => {
      try {
        const [year, month, day] = dateString.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return dateString;
      }
    };

    const getDateRange = (typedEvent: PublicTypedEvent) => {
      if (!typedEvent.dates || typedEvent.dates.length === 0) return "Dates TBD";
      const firstDate = typedEvent.dates[0].date;
      const lastDate = typedEvent.dates[typedEvent.dates.length - 1].date;
      if (firstDate === lastDate) return formatDate(firstDate);
      return `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
    };

    const getLowestPrice = (typedEvent: PublicTypedEvent) => {
      if (!typedEvent.pricing_options || typedEvent.pricing_options.length === 0) return "Free";
      const prices = typedEvent.pricing_options.map((p) => p.amount).filter((a) => a > 0);
      if (prices.length === 0) return "Free";
      const lowest = Math.min(...prices);
      return `$${lowest.toFixed(2)}`;
    };

    const getSpotsText = (typedEvent: PublicTypedEvent) => {
      if (!typedEvent.max_registrations) return null;
      const remaining = typedEvent.max_registrations - typedEvent.current_registrations;
      if (remaining <= 0) return typedEvent.waitlist_enabled ? "Waitlist available" : "Full";
      if (remaining <= 5) return `${remaining} spots left`;
      return `${remaining} spots available`;
    };

    if (loading) {
      return (
        <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
          {!embed && <Navbar />}
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-gray-500"></div>
          </div>
          {!embed && <Footer />}
        </div>
      );
    }

    return (
      <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
        {!embed && <Navbar />}
        <div className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Upcoming {T.Nouns}</h1>
            <p className="text-gray-600">
              Browse and register for our upcoming {T.nouns}.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {typedEvents.length === 0 && !error ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-500 mb-2">No {T.nouns} available</h2>
              <p className="text-gray-400">Check back later for upcoming {T.nouns}.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {typedEvents.map((typedEvent) => {
                const spotsText = getSpotsText(typedEvent);
                return (
                  <Link
                    key={typedEvent.id}
                    to={embed ? `/${clubid}/embed/${T.nouns}/${typedEvent.id}` : `/${clubid}/${T.nouns}/${typedEvent.id}`}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {typedEvent.cover_image_url && (
                      <img
                        src={typedEvent.cover_image_url}
                        alt={typedEvent.name}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {typedEvent.name}
                        </h3>
                        {typedEvent.members_only && (
                          <span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            Members Only
                          </span>
                        )}
                      </div>

                      {typedEvent.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {typedEvent.description}
                        </p>
                      )}

                      <div className="space-y-2 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{getDateRange(typedEvent)}</span>
                        </div>

                        {typedEvent.dates?.[0]?.location && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{typedEvent.dates[0].location}</span>
                          </div>
                        )}

                        {(typedEvent.age_min || typedEvent.age_max) && (
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span>
                              Ages {typedEvent.age_min || "Any"} - {typedEvent.age_max || "Any"}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-lg font-bold text-blue-600">
                          {getLowestPrice(typedEvent)}
                        </span>
                        {spotsText && (
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded-full ${
                            spotsText === "Full"
                              ? "bg-red-100 text-red-700"
                              : spotsText.includes("left")
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                          }`}
                          >
                            {spotsText}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        {!embed && <Footer />}
      </div>
    );
  };

  PublicTypedEventsListPage.displayName = `${T.Noun}PublicTypedEventsListPage`;
  return PublicTypedEventsListPage;
}
